// ??? Meld Validation & Classification (Moor200) ??????????????????????????????
//
// RULES:
//   � Sequences ONLY � no same-rank melds.
//   � Rank "2" (same suit) = natural joker. Its natural position is index 1 (A-2-3).
//     When rank-2 card is in its natural position (same suit as sequence) it does NOT
//     make the meld a Moor100 � it is treated as a regular card.
//   � Deck Joker cards (isDeckJoker=true) = always substitute jokers ? Moor100.
//   � Rank-2 used in a position OTHER than index 1 of its own suit = substitute ? Moor100.
//   � Maximum 1 substitute joker per sequence.
//   � Moor200: 7-13 fully natural cards (no substitute jokers).
//   � Moor1000: exactly 14 cards A(0)-2(1)-3(2)-...-K(12)-A(13), fully natural.
//   � Moor100: 7+ cards with at least 1 substitute joker.
//   � Opening requirement: team must have BOTH at least one Moor200 AND at least one Moor100.
//

import {
  Card,
  Meld,
  MeldType,
  SEQ_RANK_INDEX,
  GAME_CONFIG,
  type TeamId,
} from "./types";

// ??? Public API ??????????????????????????????????????????????????????????????

/**
 * Classify a set of cards as a valid meld.
 * Returns type + bonus points + suit, or null if invalid.
 */
export function classifyMeld(cards: Card[]): {
  type: MeldType;
  basePoints: number;
  suit?: string;
} | null {
  if (cards.length < GAME_CONFIG.MIN_MELD_SIZE) return null;
  return checkSequence(cards);
}

/**
 * Classify a meld and determine if it is "natural" (no substitute jokers).
 * A natural meld can be the first meld of a turn.
 */
export function isNaturalMeld(cards: Card[]): boolean {
  const result = classifyMeld(cards);
  if (!result) return false;
  // A meld is natural if it has no substitute jokers
  // moor200 and moor1000 use zero substitute jokers; plain "sequence" may also be natural
  // moor100 always has at least one substitute joker
  if (result.type === "moor100") return false;
  // Verify directly � no deck jokers and no rank-2 used as substitute
  return !hasSubstituteJoker(cards);
}

/**
 * Check if adding new cards to an existing meld is valid.
 * Returns new classification or null.
 */
export function validateAddToMeld(
  existingMeld: Meld,
  newCards: Card[]
): { type: MeldType; basePoints: number; suit?: string } | null {
  const combined = [...existingMeld.cards, ...newCards];
  return classifyMeld(combined);
}

/**
 * True if adding `newCards` would bring a substitute joker into a meld that is
 * currently natural. Adding natural cards to a meld that already holds its one
 * substitute joker does not count — the joker was already there.
 * Returns false when the combination is invalid (callers report that separately).
 */
export function addIntroducesSubstitute(existingCards: Card[], newCards: Card[]): boolean {
  const combined = [...existingCards, ...newCards];
  if (!classifyMeld(combined)) return false;
  return isNaturalMeld(existingCards) && !isNaturalMeld(combined);
}

/**
 * Normalize the order of cards in a sequence meld (used for display).
 * Returns reordered cards or null if invalid.
 */
export function tryNormalizeMeld(
  existingCards: Card[],
  addedCards: Card[]
): { cards: Card[]; type: MeldType; basePoints: number } | null {
  const allCards = [...existingCards, ...addedCards];
  const result = checkSequence(allCards);
  if (!result) return null;
  const ordered = buildOrderedSequence(allCards);
  if (!ordered) return null;
  return { cards: ordered, type: result.type, basePoints: result.basePoints };
}

/**
 * Check if a team can open (finish a round).
 * Requirement: team has AT LEAST ONE Moor200 AND AT LEAST ONE Moor100 on table,
 *              AND the player's hand is empty.
 */
export function canTeamOpen(
  melds: Meld[],
  teamId: TeamId,
  playerHandEmpty: boolean
): boolean {
  if (!playerHandEmpty) return false;
  const teamMelds = melds.filter((m) => m.teamId === teamId);
  const hasMoor200 = teamMelds.some(
    (m) => m.meldType === "moor200" || m.meldType === "moor1000"
  );
  const hasMoor100 = teamMelds.some((m) => m.meldType === "moor100");
  return hasMoor200 && hasMoor100;
}

/**
 * True if `topCard` plus some cards from `hand` can form a NATURAL sequence
 * (no substitute jokers) of at least 3 cards.
 *
 * Equivalent to trying every subset of the hand with classifyMeld + isNaturalMeld,
 * but linear: any natural sequence containing the top card also contains a
 * 3-card natural window containing it, so it is enough to look for one window.
 */
export function canFormNaturalSequenceWith(hand: Card[], topCard: Card): boolean {
  if (topCard.isDeckJoker) return false;
  const suit = topCard.suit;

  // Positions in the 0..13 sequence the hand can fill naturally (same suit only;
  // a same-suit rank-2 sits at index 1, an ace can sit low or high).
  const handPositions = new Set<number>();
  for (const c of hand) {
    if (c.isDeckJoker || c.suit !== suit) continue;
    if (c.rank === "A") {
      handPositions.add(0);
      handPositions.add(13);
    } else {
      handPositions.add(SEQ_RANK_INDEX[c.rank]!);
    }
  }

  const topPositions = topCard.rank === "A" ? [0, 13] : [SEQ_RANK_INDEX[topCard.rank]!];
  for (const t of topPositions) {
    // A 3-card window never contains both 0 and 13, so one ace card is never counted twice.
    for (let start = Math.max(0, t - 2); start <= Math.min(t, 11); start++) {
      let ok = true;
      for (let p = start; p < start + 3; p++) {
        if (p !== t && !handPositions.has(p)) { ok = false; break; }
      }
      if (ok) return true;
    }
  }
  return false;
}

/**
 * Check if a team has a Moor200 (or Moor1000) on the table.
 */
export function teamHasMoor200(melds: Meld[], teamId: TeamId): boolean {
  return melds.some(
    (m) =>
      m.teamId === teamId &&
      (m.meldType === "moor200" || m.meldType === "moor1000")
  );
}

/**
 * Check if a team has a Moor100 on the table.
 */
export function teamHasMoor100(melds: Meld[], teamId: TeamId): boolean {
  return melds.some((m) => m.teamId === teamId && m.meldType === "moor100");
}

// ??? Internal Helpers ????????????????????????????????????????????????????????

/**
 * Determine if a card set contains any substitute joker usage.
 * Substitute jokers are:
 *   - Deck jokers (isDeckJoker), OR
 *   - Rank-2 cards of a DIFFERENT suit from the sequence.
 * Rank-2 cards of the SAME suit are always natural (they sit at index 1 = their
 * actual rank), whether the sequence is A-2-3 or 2-3-4 or anything starting from 2.
 */
function hasSubstituteJoker(cards: Card[]): boolean {
  if (cards.some((c) => c.isDeckJoker)) return true;

  // Determine sequence suit from definite non-rank-2, non-deck-joker cards
  const baseCards = cards.filter((c) => !c.isDeckJoker && c.rank !== "2");
  if (baseCards.length === 0) return false;
  const suit = baseCards[0].suit;

  const rank2Cards = cards.filter((c) => c.rank === "2" && !c.isDeckJoker);

  // Rank-2 of a different suit = always substitute
  if (rank2Cards.some((c) => c.suit !== suit)) return true;

  // Same-suit rank-2: natural only if every card fits at its own rank with no gaps
  // and no duplicates, for SOME ace placement (A-low, A-high, or one of each for
  // Moor1000). Otherwise rank-2 must be acting as a substitute joker.
  const sameSuitRank2 = rank2Cards.filter((c) => c.suit === suit);
  if (sameSuitRank2.length > 0) {
    const regularCards = [...baseCards, ...sameSuitRank2];
    const fitsNaturally = aceIndexConfigs(regularCards).some(
      (idxs) => trySequenceWithIndices(idxs, 0, cards.length, suit) !== null
    );
    if (!fitsNaturally) return true;
  }

  return false;
}

/**
 * All index assignments for a set of fixed cards, varying only where aces sit:
 * all A-low (0), all A-high (13), and — with two aces — one low and one high.
 */
function aceIndexConfigs(regular: Card[]): number[][] {
  const aceCount = regular.filter((c) => c.rank === "A").length;
  const configs: number[][] = [];
  function makeConfig(getAceIdx: () => number) {
    configs.push(
      regular.map((c) => (c.rank === "A" ? getAceIdx() : SEQ_RANK_INDEX[c.rank]!))
    );
  }
  makeConfig(() => 0);
  if (aceCount >= 1) makeConfig(() => 13);
  if (aceCount >= 2) {
    let first = true;
    makeConfig(() => { if (first) { first = false; return 0; } return 13; });
  }
  return configs;
}

/**
 * Core sequence classifier.
 *
 * Joker handling:
 *   - Deck jokers (isDeckJoker): always substitute.
 *   - Rank-2 same suit: PREFERRED as natural card at index 1 (A-2-3 position).
 *     BUT if natural placement leaves unfillable gaps (e.g. extending 2-3-4 with 6-7
 *     requires rank-2 to move to fill the gap at 5), rank-2 may alternatively act
 *     as a substitute joker — enabling sequences like 3-4-[2★]-6-7.
 *   - Rank-2 different suit: always substitute joker.
 *   - Max 1 substitute joker per sequence.
 */
function checkSequence(
  cards: Card[]
): { type: MeldType; basePoints: number; suit: string } | null {
  const deckJokers = cards.filter((c) => c.isDeckJoker);

  // Base cards: not deck jokers, not rank-2 (rank-2 handled separately)
  const baseCards = cards.filter((c) => !c.isDeckJoker && c.rank !== "2");

  if (baseCards.length === 0) return null; // need at least one definite card to get suit

  // All base cards must share the same suit
  const suit = baseCards[0].suit;
  if (!baseCards.every((c) => c.suit === suit)) return null;

  // Rank-2 cards (non-deck-joker)
  const rank2Cards = cards.filter((c) => c.rank === "2" && !c.isDeckJoker);
  const sameSuitRank2 = rank2Cards.filter((c) => c.suit === suit);
  const diffSuitRank2 = rank2Cards.filter((c) => c.suit !== suit);

  // Substitute count: deck jokers + diff-suit rank-2
  const alwaysSubCount = deckJokers.length + diffSuitRank2.length;
  if (alwaysSubCount > 1) return null; // max 1 substitute

  const configs: { idxs: number[]; subCount: number; rank2AsSub?: boolean }[] = [];

  // --- Config builder helper ---
  function addConfigs(regular: Card[], subCount: number, rank2AsSub?: boolean) {
    for (const idxs of aceIndexConfigs(regular)) {
      configs.push({ idxs, subCount, rank2AsSub });
    }
  }

  // Config 1: same-suit rank-2 as NATURAL card at index 1 (preferred)
  const regularNatural = [...baseCards, ...sameSuitRank2];
  addConfigs(regularNatural, alwaysSubCount, false);

  // Config 2: same-suit rank-2 as SUBSTITUTE (allows it to fill gaps elsewhere)
  // Only possible if alwaysSubCount is 0 (would become 1, still ≤ max)
  if (sameSuitRank2.length > 0 && alwaysSubCount === 0) {
    addConfigs(baseCards, 1, true);
  }

  // Config 3: when there are 2 same-suit rank-2 cards (two decks), ONE sits at its
  // natural position (index 1) and the other fills a gap as a substitute.
  // Total substitutes = alwaysSubCount + 1 must be ≤ 1.
  if (sameSuitRank2.length >= 2 && alwaysSubCount === 0) {
    // Use one rank-2 as natural (included in regular cards at index 1),
    // the remaining one(s) count as substitute jokers.
    const remainingSubs = sameSuitRank2.length - 1; // = 1 for two rank-2 cards
    if (remainingSubs <= 1) {
      addConfigs([...baseCards, sameSuitRank2[0]], remainingSubs, false);
    }
  }

  const priority: Record<string, number> = {
    moor1000: 4, moor200: 3, moor100: 2, sequence: 1,
  };
  // Natural configs have higher priority than substitute configs when types are equal
  let best: { type: MeldType; basePoints: number; suit: string; rank2AsSub?: boolean } | null = null;

  for (const cfg of configs) {
    const result = trySequenceWithIndices(cfg.idxs, cfg.subCount, cards.length, suit);
    if (result) {
      const newPri = priority[result.type] ?? 0;
      const bestPri = best ? (priority[best.type] ?? 0) : -1;
      // Prefer higher type; on tie prefer natural (rank2AsSub=false) over substitute
      if (newPri > bestPri || (newPri === bestPri && !cfg.rank2AsSub && best?.rank2AsSub)) {
        best = { ...result, rank2AsSub: cfg.rank2AsSub };
      }
    }
  }

  return best ? { type: best.type, basePoints: best.basePoints, suit: best.suit } : null;
}

/**
 * Validate a sequence given indices of fixed (regular) cards and a substitute count.
 */
function trySequenceWithIndices(
  fixedIdxs: number[],
  subCount: number,
  totalCards: number,
  suit: string
): { type: MeldType; basePoints: number; suit: string } | null {
  if (fixedIdxs.length === 0) return null;

  const indices = [...fixedIdxs].sort((a, b) => a - b);

  // No duplicates
  for (let i = 1; i < indices.length; i++) {
    if (indices[i] === indices[i - 1]) return null;
  }

  const minIdx = indices[0];
  const maxIdx = indices[indices.length - 1];
  const span = maxIdx - minIdx + 1;
  const gaps = span - indices.length; // holes needing substitutes

  if (gaps > subCount) return null;

  const extraSubs = subCount - gaps; // substitutes extending beyond span
  const totalLength = span + extraSubs;

  if (totalLength !== totalCards) return null;
  if (totalLength > 14) return null;
  if (totalLength < GAME_CONFIG.MIN_MELD_SIZE) return null;

  // Moor1000: exactly 14-card A-to-A sequence, rank-2 at index 1
  const hasIndex1 = indices.includes(1);

  // Classify
  if (subCount > 0) {
    if (totalLength >= GAME_CONFIG.MOOR_SIZE) {
      return { type: "moor100", basePoints: GAME_CONFIG.MOOR100_BONUS, suit };
    }
    return { type: "sequence", basePoints: 0, suit };
  }

  // No substitute jokers � fully natural
  if (totalLength === 14 && hasIndex1) {
    return { type: "moor1000", basePoints: GAME_CONFIG.MOOR1000_BONUS, suit };
  }
  if (totalLength >= GAME_CONFIG.MOOR_SIZE) {
    return { type: "moor200", basePoints: GAME_CONFIG.MOOR200_BONUS, suit };
  }
  return { type: "sequence", basePoints: 0, suit };
}

/**
 * Build an ordered card array for a valid sequence (for display normalization).
 */
function buildOrderedSequence(cards: Card[]): Card[] | null {
  const deckJokers = cards.filter((c) => c.isDeckJoker);
  const baseCards = cards.filter((c) => !c.isDeckJoker && c.rank !== "2");
  if (baseCards.length === 0) return null;
  const suit = baseCards[0].suit;
  if (!baseCards.every((c) => c.suit === suit)) return null;

  const rank2Cards = cards.filter((c) => c.rank === "2" && !c.isDeckJoker);
  const sameSuitRank2 = rank2Cards.filter((c) => c.suit === suit);
  const diffSuitRank2 = rank2Cards.filter((c) => c.suit !== suit);

  // Determine whether same-suit rank-2 should act as natural or substitute:
  // Natural: place at index 1; if that leaves unfillable gaps, use as substitute.
  function tryBuild(useRank2AsSub: boolean): Card[] | null {
    const subJokers = [...deckJokers, ...diffSuitRank2, ...(useRank2AsSub ? sameSuitRank2 : [])];
    const regularCards = useRank2AsSub ? [...baseCards] : [...baseCards, ...sameSuitRank2];
    const idxs: number[] = regularCards.map((c) => (c.rank === "A" ? 0 : SEQ_RANK_INDEX[c.rank]!));

    const indexToCard = new Map<number, Card>();
    for (let i = 0; i < regularCards.length; i++) {
      indexToCard.set(idxs[i], regularCards[i]);
    }

    const filledIdxs = [...indexToCard.keys()].sort((a, b) => a - b);
    if (filledIdxs.length === 0) return null;

    const minIdx = filledIdxs[0];
    const maxIdx = filledIdxs[filledIdxs.length - 1];
    const gaps = (maxIdx - minIdx + 1) - filledIdxs.length;
    // Check if we have enough substitutes to fill gaps
    if (gaps > subJokers.length) return null;

    const result: Card[] = [];
    let subIdx = 0;
    for (let i = minIdx; i <= maxIdx; i++) {
      if (indexToCard.has(i)) {
        result.push(indexToCard.get(i)!);
      } else if (subIdx < subJokers.length) {
        result.push(subJokers[subIdx++]);
      }
    }
    // Append any remaining substitute jokers (extending beyond span)
    while (subIdx < subJokers.length) {
      result.push(subJokers[subIdx++]);
    }
    if (result.length !== cards.length) return null;
    return result;
  }

  // Prefer natural placement; fall back to substitute if natural leaves unfillable gaps
  return tryBuild(false) ?? tryBuild(true);
}
